import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { EmailAccount } from '../../../../types/email-account';
import {
  UpdateEmailAccountRampUpSettingsRequestPayload,
  UpdateEmailAccountGeneralSettingsRequestPayload,
  EmailTimeIntervalSettingsRequest,
  EmailAccountSetupScorePayload,
} from '../../../../types/request-payload';

import { EmailAccountSettings } from '../../../../types/email-account-settings';
import {
  EmailAccountMethod,
  EmailAccountType,
} from '../../../../enums/email-account';
import { AgencyConfig } from '../../../../../home/types';
import { EmailAccountSetup } from '../../../../../../shared/components/email-account-setup-score/types';
import { AgencyClientFilter } from '../../../../../agency-client-management/types/types';
import {
  AssignAgencyClientResourceRequestPayload,
  GetAgencyClientDropdownRequestPayload,
} from '../../../../../agency-client-management/types/request-payload';
import { FeatureUsage } from '../../../../../../shared/types/feature-usage';
import { CustomDomainType } from '../../../../../settings/types/custom-domain';

export enum UpdateEmailTabKeys {
  GENERAL_SETTINGS = 'general-settings',
  SENDING_SETTINGS = 'sending-settings',
  EMAIL_SETUP_SCORE = 'email-setup-score',
}

export interface RouteParams {
  emailAccountId: string;
  tab: string;
  hashId?: string;
}

export type EmailAccountInfo = {
  name: string;
  email: string;
  accountType: string;
  isConnected: number;
  method: EmailAccountMethod;
};

// Sending Settings Props

export type WarmupDetails = {
  dailySendingLimit: number;
  availableQuota: number;
  Id: number;
};

export type RampUpSettings = {
  rampUpStatus: number;
  rampUpPercent: number;
  initialSendingLimit: number;
  rampUpLimit: string;
  maxSendingLimit: string;
  rampUpLimitReached: boolean;
  minInterval: number;
  maxInterval: number;
};

export type SendingSettingsProps = {
  emailAccountId: number;
  emailAccountStatus: number;
  emailAccountType: EmailAccountType;
  rampUpSettings: RampUpSettings;
  onSave: (payload: UpdateEmailAccountRampUpSettingsRequestPayload) => void;
  rampUpSettingsRequestStatus: string;
  rampUpSettingsRequestMessage: string;
  rampUpSettingsRequestError: any;
  sendGetEmailAccountSettingsRequest: (id: string | number) => void;
  sendEmailTimeIntervalRequest: (
    emailAccountId: number,
    payload: EmailTimeIntervalSettingsRequest,
  ) => void;
  resetEmailAccountRampUpSettingsRequestState: () => void;
  agencyConfig: Partial<AgencyConfig>;
  emailTimeIntervalSettingsRequestStatus: RequestStatus;
  emailTimeIntervalSettingsRequestMessage: string;
  sendGetEmailAccountSetupScoreRequest: (
    payload: EmailAccountSetupScorePayload,
  ) => void;
  featureUsages: FeatureUsage[];
};

// Email Account Setup Props

export enum ScoreFactorStatus {
  Valid = 'valid',
  PartiallyValid = 'partially-valid',
  NotValid = 'not-valid',
  InProgress = 'in-progress',
  NotFound = 'not-found',
}

export type EmailAccountSetupProps = {
  emailAccountId: number;
  emailAccountSetup: EmailAccountSetup;
  userTimezone: string;
  agencyConfig: Partial<AgencyConfig>;
  getEmailAccountSetupScoreRequest: RequestStatus;
  getEmailAccountSetupScoreRequestMessage: string;
  getEmailAccountSetupScoreRequestError: any;
  sendGetEmailAccountSetupScoreRequest: (
    payload: EmailAccountSetupScorePayload,
  ) => void;
  resetEmailAccountSetupState: VoidFunction;
};

// General Settings Props And State

type StateValues = {
  senderName?: string;
  senderFirstName: string;
  senderLastName?: string;
  bcc: string;
  signature: string;
};

type StateErrors = { [P in keyof Required<StateValues>]: string };
type StateDirty = { [P in keyof StateErrors]: boolean };

export type GeneralSettingsState = {
  values: StateValues;
  errors: StateErrors;
  dirty: StateDirty;
  selectedCustomDomain: {
    key: number;
    value: string;
  };
  selectedAgencyClient: AgencyClientFilter;
  isCustomDomainOptionsFetched: boolean;
  isAgencyClientsOptionsFetched: boolean;
};

export type GeneralSettingsProps = {
  emailAccountId: number;
  hashId: string;
  emailAccount: EmailAccount;
  tabKey: UpdateEmailTabKeys | string;
  emailAccountSettings: EmailAccountSettings[];
  sendUpdateEmailAccountRequest: (
    emailAccountId: number,
    payload: UpdateEmailAccountGeneralSettingsRequestPayload,
  ) => void;
  updateEmailAccountRequestStatus: RequestStatus;
  getEmailAccountRequestStatus: RequestStatus;
  customDomainList: CustomDomainType[];
  sendImageUploadRequest: (formData, onUploadProgress) => void;
  t: (x: string) => React.ReactNode;
  onCheckEmailScore: (payload: EmailAccountSetupScorePayload) => void;
  sendGetEmailAccountRequest: (id: string | number) => void;
  sendGetEmailAccountSettingsRequest: (id: string | number) => void;
  sendGetCustomDomainsListRequest: () => void;
  agencyClients: AgencyClientFilter[];
  selectedAgencyClient: AgencyClientFilter;
  sendUpdateAssignAgencyClientResource: (
    payload: AssignAgencyClientResourceRequestPayload,
  ) => void;
  getCustomDomainsListRequestStatus: RequestStatus;
  sendGetAgencyClientsDropdownRequest: (
    payload: GetAgencyClientDropdownRequestPayload,
  ) => void;
  getAgencyClientsDropdownRequestStatus: RequestStatus;
  setIsSignatureDirty: (isDirty: boolean) => void;
  isSignatureDirty: boolean;
};

export type CustomDomainDropdown = CustomDomainType & { key: number };
